import { Plus, Building2, Star, StarOff, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { useAdmin } from './AdminContext';

export const AdminBusinessesTab = () => {
  const { localBusinesses, businessDialogOpen, setBusinessDialogOpen, newBusiness, setNewBusiness, createBusiness, deleteBusiness, featureItem, unfeatureItem } = useAdmin();

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Local Businesses ({localBusinesses.length})</h2>
        <Dialog open={businessDialogOpen} onOpenChange={setBusinessDialogOpen}>
          <DialogTrigger asChild><Button><Plus size={14} className="mr-1" /> Add Business</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Add Local Business</DialogTitle><DialogDescription>Add a restaurant, store, or local business manually</DialogDescription></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label>Business Name *</Label><Input value={newBusiness.name} onChange={(e) => setNewBusiness({...newBusiness, name: e.target.value})} placeholder="Business name" /></div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={newBusiness.category} onValueChange={(v) => setNewBusiness({...newBusiness, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restaurant">Restaurant</SelectItem>
                    <SelectItem value="retail">Retail Store</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="entertainment">Entertainment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={newBusiness.description} onChange={(e) => setNewBusiness({...newBusiness, description: e.target.value})} placeholder="Brief description" rows={2} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Select value={newBusiness.city} onValueChange={(v) => setNewBusiness({...newBusiness, city: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Minneapolis">Minneapolis</SelectItem><SelectItem value="St. Paul">St. Paul</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Phone</Label><Input value={newBusiness.phone} onChange={(e) => setNewBusiness({...newBusiness, phone: e.target.value})} placeholder="(612) 555-0123" /></div>
              </div>
              <div className="space-y-2"><Label>Address</Label><Input value={newBusiness.address} onChange={(e) => setNewBusiness({...newBusiness, address: e.target.value})} placeholder="123 Main St" /></div>
              <div className="space-y-2"><Label>Website</Label><Input value={newBusiness.website} onChange={(e) => setNewBusiness({...newBusiness, website: e.target.value})} placeholder="https://..." /></div>
              <div className="space-y-2"><Label>Image URL</Label><Input value={newBusiness.image_url} onChange={(e) => setNewBusiness({...newBusiness, image_url: e.target.value})} placeholder="https://... (direct image URL)" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBusinessDialogOpen(false)}>Cancel</Button>
              <Button onClick={createBusiness} disabled={!newBusiness.name}>Create Business</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {localBusinesses.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Building2 size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No businesses yet</h3>
            <p className="text-muted-foreground mb-4">Add local restaurants, stores, and businesses.</p>
            <Button onClick={() => setBusinessDialogOpen(true)}><Plus size={14} className="mr-1" /> Add First Business</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {localBusinesses.map(business => (
            <Card key={business.id} className="bg-card border-border">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{business.name}</CardTitle>
                  {business.is_featured && <Badge className="bg-yellow-500/20 text-yellow-500"><Star size={12} className="mr-1" />Featured</Badge>}
                </div>
                <CardDescription className="capitalize">{business.category} - {business.city}</CardDescription>
              </CardHeader>
              <CardContent>
                {business.image_url && <img src={business.image_url} alt={business.name} className="w-full h-32 object-cover rounded-sm mb-3" />}
                {business.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{business.description}</p>}
                <div className="flex flex-wrap gap-2">
                  {!business.is_featured ? (
                    <Button size="sm" variant="outline" onClick={() => featureItem('business', business.id)}><Star size={14} className="mr-1" /> Feature</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => unfeatureItem('business', business.id)}><StarOff size={14} className="mr-1" /> Unfeature</Button>
                  )}
                  <Button size="sm" variant="destructive" onClick={() => deleteBusiness(business.id)}><Trash2 size={14} className="mr-1" /> Delete</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};
